import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { useAuth } from '../context/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from '../components/ui/drawer'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { Textarea } from '../components/ui/textarea'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp'
import { 
  Shield, Lock, Unlock, FileText, Upload, Trash2, Eye, 
  Clock, Loader2, RefreshCw, Plus, AlertTriangle, Check, Timer
} from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

export default function VaultPage() {
  const { t, locale } = useLanguage()
  const { token } = useAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const lang = locale?.startsWith('es') ? 'es' : 'en'
  
  // Vault state
  const [isUnlocked, setIsUnlocked] = useState(false)
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [documentCount, setDocumentCount] = useState(0)
  const [maxDocuments, setMaxDocuments] = useState(50)
  
  // OTP state
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [verifyingOtp, setVerifyingOtp] = useState(false)
  
  // Documents state
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Upload state
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    expiration_date: '',
    notes: ''
  })
  
  // View file state
  const [viewingFile, setViewingFile] = useState(false)
  
  // Edit/View state
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [showDocumentDetail, setShowDocumentDetail] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null })

  // Timer for session countdown
  useEffect(() => {
    let interval
    if (isUnlocked && sessionExpiresAt) {
      interval = setInterval(() => {
        const now = new Date()
        const expires = new Date(sessionExpiresAt)
        const remaining = Math.max(0, Math.floor((expires - now) / 1000))
        setRemainingSeconds(remaining)
        
        if (remaining === 0) {
          setIsUnlocked(false)
          setSessionExpiresAt(null)
          toast.info(lang === 'es' ? 'Sesión expirada. Desbloquea de nuevo.' : 'Session expired. Unlock again.')
        }
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isUnlocked, sessionExpiresAt, lang])

  // Fetch vault status on mount
  useEffect(() => {
    checkVaultStatus()
  }, [token])

  // Fetch documents when unlocked
  useEffect(() => {
    if (isUnlocked) {
      fetchDocuments()
    }
  }, [isUnlocked])

  const checkVaultStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/vault/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setIsUnlocked(res.data.is_unlocked)
      setSessionExpiresAt(res.data.session_expires_at)
      setRemainingSeconds(res.data.remaining_seconds)
      setDocumentCount(res.data.document_count)
      setMaxDocuments(res.data.max_documents)
    } catch (error) {
      console.error('Error checking vault status:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/api/vault/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setDocuments(res.data.documents || [])
    } catch (error) {
      if (error.response?.status === 403) {
        setIsUnlocked(false)
      }
      console.error('Error fetching documents:', error)
    }
  }

  const handleRequestOtp = async () => {
    try {
      setSendingOtp(true)
      await axios.post(`${API}/api/vault/request-otp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setOtpSent(true)
      toast.success(lang === 'es' ? 'Código enviado a tu email' : 'Code sent to your email')
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'es' ? 'Error al enviar código' : 'Error sending code'))
    } finally {
      setSendingOtp(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      toast.error(lang === 'es' ? 'Ingresa el código de 6 dígitos' : 'Enter the 6-digit code')
      return
    }
    
    try {
      setVerifyingOtp(true)
      const res = await axios.post(`${API}/api/vault/unlock`, 
        { code: otpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setIsUnlocked(true)
      setSessionExpiresAt(res.data.session_expires_at)
      setShowOtpModal(false)
      setOtpCode('')
      setOtpSent(false)
      toast.success(lang === 'es' ? 'Bóveda desbloqueada' : 'Vault unlocked')
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'es' ? 'Código incorrecto' : 'Incorrect code'))
    } finally {
      setVerifyingOtp(false)
    }
  }

  const handleLockVault = async () => {
    try {
      await axios.post(`${API}/api/vault/lock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setIsUnlocked(false)
      setSessionExpiresAt(null)
      setDocuments([])
      toast.success(lang === 'es' ? 'Bóveda bloqueada' : 'Vault locked')
    } catch (error) {
      toast.error(lang === 'es' ? 'Error al bloquear' : 'Error locking')
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        toast.error(lang === 'es' ? 'Solo PDF e imágenes' : 'Only PDF and images')
        return
      }
      // Validate size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(lang === 'es' ? 'Máximo 10MB' : 'Maximum 10MB')
        return
      }
      setSelectedFile(file)
      setUploadForm(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }))
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.title.trim()) {
      toast.error(lang === 'es' ? 'Selecciona archivo y título' : 'Select file and title')
      return
    }
    
    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('title', uploadForm.title)
      formData.append('description', uploadForm.description || '')
      formData.append('expiration_date', uploadForm.expiration_date || '')
      formData.append('notes', uploadForm.notes || '')
      
      await axios.post(`${API}/api/vault/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      })
      
      toast.success(lang === 'es' ? 'Documento guardado' : 'Document saved')
      setShowUploadForm(false)
      resetUploadForm()
      fetchDocuments()
      checkVaultStatus()
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'es' ? 'Error al subir' : 'Upload error'))
    } finally {
      setUploading(false)
    }
  }

  const resetUploadForm = () => {
    setSelectedFile(null)
    setUploadForm({
      title: '',
      description: '',
      expiration_date: '',
      notes: ''
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Handle viewing document file with proper authentication
  const handleViewFile = async (doc) => {
    try {
      setViewingFile(true)
      
      // Fetch the file with authentication header
      const response = await axios.get(`${API}${doc.file_url}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      
      // Create blob URL and open it
      const blob = new Blob([response.data], { type: doc.file_type })
      const blobUrl = URL.createObjectURL(blob)
      
      // Open in new window/tab
      const newWindow = window.open(blobUrl, '_blank')
      
      // For Safari/iOS compatibility - if window.open fails, create a download link
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Fallback: create download link
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = doc.title + '.' + (doc.file_type.split('/')[1] || 'pdf')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        toast.info(lang === 'es' ? 'Archivo descargado' : 'File downloaded')
      }
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000)
      
    } catch (error) {
      console.error('Error viewing file:', error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error(lang === 'es' ? 'Sesión expirada. Desbloquea de nuevo.' : 'Session expired. Unlock again.')
        setIsUnlocked(false)
      } else {
        toast.error(lang === 'es' ? 'Error al abrir archivo' : 'Error opening file')
      }
    } finally {
      setViewingFile(false)
    }
  }

  const handleViewDocument = async (doc) => {
    setSelectedDocument(doc)
    setShowDocumentDetail(true)
  }

  const handleDeleteDocument = async () => {
    const doc = deleteDialog.document
    if (!doc) return
    
    try {
      await axios.delete(`${API}/api/vault/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      toast.success(lang === 'es' ? 'Documento eliminado' : 'Document deleted')
      setDeleteDialog({ open: false, document: null })
      fetchDocuments()
      checkVaultStatus()
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'es' ? 'Error al eliminar' : 'Delete error'))
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  // Locked state - show unlock screen
  if (!isUnlocked) {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <Card className="border-2 border-emerald-200 dark:border-emerald-800 bg-gradient-to-b from-emerald-50/50 to-white dark:from-emerald-900/20 dark:to-gray-900">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
              <Shield className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            </div>
            
            <h1 className="text-2xl font-bold mb-2">
              {lang === 'es' ? 'Bóveda de Documentos' : 'Document Vault'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {lang === 'es' ? 'Zona protegida' : 'Protected zone'}
            </p>
            
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-6 flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300 text-left">
                {lang === 'es' 
                  ? 'Tus documentos importantes están protegidos. Desbloquea con un código OTP enviado a tu email.'
                  : 'Your important documents are protected. Unlock with an OTP code sent to your email.'}
              </p>
            </div>
            
            <Button
              size="lg"
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowOtpModal(true)}
              data-testid="unlock-vault-btn"
            >
              <Unlock className="w-5 h-5 mr-2" />
              {lang === 'es' ? 'Desbloquear con OTP' : 'Unlock with OTP'}
            </Button>
            
            <p className="text-xs text-muted-foreground mt-4">
              {documentCount} / {maxDocuments} {lang === 'es' ? 'documentos' : 'documents'}
            </p>
          </CardContent>
        </Card>

        {/* OTP Modal */}
        <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                {lang === 'es' ? 'Verificación OTP' : 'OTP Verification'}
              </DialogTitle>
              <DialogDescription>
                {lang === 'es' 
                  ? 'Ingresa el código de 6 dígitos enviado a tu email'
                  : 'Enter the 6-digit code sent to your email'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!otpSent ? (
                <Button
                  className="w-full"
                  onClick={handleRequestOtp}
                  disabled={sendingOtp}
                  data-testid="request-otp-btn"
                >
                  {sendingOtp ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {lang === 'es' ? 'Enviar código a mi email' : 'Send code to my email'}
                </Button>
              ) : (
                <>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={6}
                      value={otpCode}
                      onChange={setOtpCode}
                      data-testid="otp-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || otpCode.length !== 6}
                    data-testid="verify-otp-btn"
                  >
                    {verifyingOtp ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    {lang === 'es' ? 'Verificar y desbloquear' : 'Verify and unlock'}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="w-full text-sm"
                    onClick={handleRequestOtp}
                    disabled={sendingOtp}
                  >
                    {lang === 'es' ? 'Reenviar código' : 'Resend code'}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Unlocked state - show documents
  return (
    <div className="container max-w-2xl mx-auto px-4 py-6 pb-24 space-y-4">
      {/* Header with session timer */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-600" />
            {lang === 'es' ? 'Bóveda' : 'Vault'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lang === 'es' ? 'Documentos importantes' : 'Important documents'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Session timer badge */}
          <Badge 
            variant="outline" 
            className={`flex items-center gap-1 ${remainingSeconds < 120 ? 'border-red-300 text-red-600' : 'border-emerald-300 text-emerald-600'}`}
          >
            <Timer className="w-3 h-3" />
            {formatTime(remainingSeconds)}
          </Badge>
          <Button variant="ghost" size="icon" onClick={fetchDocuments}>
            <RefreshCw className="w-5 h-5" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleLockVault}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="lock-vault-btn"
          >
            <Lock className="w-4 h-4 mr-1" />
            {lang === 'es' ? 'Bloquear' : 'Lock'}
          </Button>
        </div>
      </div>

      {/* Add document button */}
      <Button
        className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700"
        onClick={() => setShowUploadForm(true)}
        disabled={documentCount >= maxDocuments}
        data-testid="add-document-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        {lang === 'es' ? 'Agregar documento' : 'Add document'}
      </Button>

      {/* Document count */}
      <div className="text-sm text-muted-foreground text-center">
        {documentCount} / {maxDocuments} {lang === 'es' ? 'documentos' : 'documents'}
      </div>

      {/* Documents list */}
      {documents.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold mb-2">
              {lang === 'es' ? 'Sin documentos' : 'No documents'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {lang === 'es' 
                ? 'Agrega tu primer documento importante'
                : 'Add your first important document'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <Card 
              key={doc.id} 
              className="cursor-pointer hover:shadow-md transition-all"
              onClick={() => handleViewDocument(doc)}
              data-testid={`document-${doc.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{doc.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{(doc.file_size / 1024 / 1024).toFixed(2)} MB</span>
                      {doc.expiration_date && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(doc.expiration_date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {doc.file_type.split('/')[1]?.toUpperCase() || 'FILE'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Form Drawer */}
      <Drawer open={showUploadForm} onOpenChange={setShowUploadForm}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              {lang === 'es' ? 'Nuevo documento' : 'New document'}
            </DrawerTitle>
          </DrawerHeader>
          
          <div className="px-4 pb-4 space-y-4 overflow-y-auto">
            {/* File selection */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Archivo' : 'File'} *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full h-24 border-dashed"
                onClick={() => fileInputRef.current?.click()}
                data-testid="select-file-btn"
              >
                {selectedFile ? (
                  <div className="text-center">
                    <FileText className="w-8 h-8 mx-auto mb-1 text-emerald-600" />
                    <span className="text-sm truncate max-w-[200px] block">{selectedFile.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-1 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {lang === 'es' ? 'Seleccionar archivo' : 'Select file'}
                    </span>
                    <span className="text-xs text-muted-foreground block">
                      PDF, JPG, PNG (máx 10MB)
                    </span>
                  </div>
                )}
              </Button>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Título' : 'Title'} *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder={lang === 'es' ? 'Nombre del documento' : 'Document name'}
                data-testid="document-title"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Descripción' : 'Description'}</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder={lang === 'es' ? 'Descripción opcional...' : 'Optional description...'}
                className="h-20 resize-none"
              />
            </div>

            {/* Expiration date */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Fecha de vencimiento' : 'Expiration date'}</Label>
              <Input
                type="date"
                value={uploadForm.expiration_date}
                onChange={(e) => setUploadForm({ ...uploadForm, expiration_date: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Notas' : 'Notes'}</Label>
              <Textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                placeholder={lang === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                className="h-20 resize-none"
              />
            </div>
          </div>

          <DrawerFooter>
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !uploadForm.title.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="upload-document-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {lang === 'es' ? 'Guardar documento' : 'Save document'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Document Detail Dialog */}
      <Dialog open={showDocumentDetail} onOpenChange={setShowDocumentDetail}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              {selectedDocument?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDocument && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{lang === 'es' ? 'Tipo' : 'Type'}</span>
                  <Badge variant="secondary">{selectedDocument.file_type.split('/')[1]?.toUpperCase()}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{lang === 'es' ? 'Tamaño' : 'Size'}</span>
                  <span>{(selectedDocument.file_size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
                {selectedDocument.expiration_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">{lang === 'es' ? 'Vence' : 'Expires'}</span>
                    <span>{new Date(selectedDocument.expiration_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              
              {selectedDocument.description && (
                <div>
                  <Label className="text-muted-foreground">{lang === 'es' ? 'Descripción' : 'Description'}</Label>
                  <p className="text-sm mt-1">{selectedDocument.description}</p>
                </div>
              )}
              
              {selectedDocument.notes && (
                <div>
                  <Label className="text-muted-foreground">{lang === 'es' ? 'Notas' : 'Notes'}</Label>
                  <p className="text-sm mt-1">{selectedDocument.notes}</p>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => handleViewFile(selectedDocument)}
                  disabled={viewingFile}
                  data-testid="view-file-btn"
                >
                  {viewingFile ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  {lang === 'es' ? 'Ver archivo' : 'View file'}
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => {
                    setShowDocumentDetail(false)
                    setDeleteDialog({ open: true, document: selectedDocument })
                  }}
                  data-testid="delete-document-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {lang === 'es' ? '¿Eliminar documento?' : 'Delete document?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === 'es' 
                ? `"${deleteDialog.document?.title}" será eliminado permanentemente.`
                : `"${deleteDialog.document?.title}" will be permanently deleted.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lang === 'es' ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteDocument}
            >
              {lang === 'es' ? 'Eliminar' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
